import { useState } from 'react'
import { useCreateRule, useFields, useRules } from '@/features/admin/access/hooks'
import { RuleEditor } from './RuleEditor'
import { formatError } from './formatError'

const OBJECT_TYPES = [
  { code: 'contract', label: 'Umowy' },
]

export function VisibilityPanel({ roleCode }: { roleCode: string }) {
  const [activeType, setActiveType] = useState(OBJECT_TYPES[0].code)
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 border-b">
        {OBJECT_TYPES.map(t => (
          <button
            key={t.code}
            type="button"
            onClick={() => setActiveType(t.code)}
            className={`text-sm px-3 py-1.5 border-b-2 ${
              activeType === t.code ? 'border-sky-600 text-sky-700 font-medium' : 'border-transparent text-slate-600'
            }`}
          >{t.label}</button>
        ))}
      </div>
      <ObjectVisibility roleCode={roleCode} objectType={activeType} />
    </div>
  )
}

function ObjectVisibility({ roleCode, objectType }: { roleCode: string; objectType: string }) {
  const fieldsQ = useFields(objectType)
  const rulesQ = useRules(roleCode, objectType)
  const create = useCreateRule(roleCode, objectType)
  const [error, setError] = useState<string | null>(null)

  const addRule = () => {
    setError(null)
    create.mutate(
      {
        name: `Reguła ${(rulesQ.data?.length ?? 0) + 1}`,
        predicate: { type: 'true' },
      },
      { onError: e => setError(formatError(e)) },
    )
  }

  if (fieldsQ.isLoading || rulesQ.isLoading) return <p className="text-xs text-slate-500">Ładowanie…</p>
  if (!fieldsQ.data || !rulesQ.data) return null

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Wszystkie reguły (z każdej roli użytkownika) są łączone <strong>I</strong> - każda musi być spełniona.
          {' '}Aby uzyskać efekt "lub" wbuduj alternatywę bezpośrednio w predykat (przełącznik I/LUB na grupie).
          {' '}Brak reguł = nie widzi nic (secure default).
        </p>
        <button
          type="button"
          onClick={addRule}
          disabled={create.isPending}
          className="text-xs bg-sky-600 text-white rounded px-3 py-1.5 disabled:opacity-50 whitespace-nowrap"
        >+ Nowa reguła</button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {rulesQ.data.length === 0 && (
        <p className="text-sm text-slate-500 italic py-4">
          Brak reguł - użytkownicy z tą rolą nie widzą żadnych obiektów typu <code>{objectType}</code>.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {rulesQ.data.map(rule => (
          <RuleEditor
            key={rule.id}
            roleCode={roleCode}
            objectType={objectType}
            rule={rule}
            metadata={fieldsQ.data}
          />
        ))}
      </div>
    </div>
  )
}
